import loadable from "@loadable/component";
import React from "react";
import { Router, Route, Switch, Redirect } from "react-router-dom";

import { browserHistory } from "js/react/history";
import { FirebaseAuthProvider } from "js/react/views/Auth/FirebaseAuthContext";
import { FirebaseAuthInitalizer } from "js/react/views/Auth/FirebaseAuthInitalizer";
import { Authenticate } from "js/react/views/Auth/Authenticate";
import { serverContext } from "js/config";
import SsoConsume from "./SsoConsume";
import Logout from "./Logout";
import Onboarding from "js/react/views/Onboarding/Onboarding";
import { VerifyEmailReturn } from "js/react/views/Onboarding/VerifyEmailReturn";
import sanitizeRelativeUrl from "common/utils/sanitizeRelativeUrl";

const Loading = () => {
    return (
        // TODO: spinner
        <div></div>
    );
};

const LoadableEditorAndLibrary = loadable(() => import(/* webpackChunkName: "editor" */ "./EditorAndLibrary"), {
    fallback: <Loading />
});

const LoadablePlayer = loadable(() => import(/* webpackChunkName: "player" */ "./Player"), {
    fallback: <Loading />
});

const LoadablePresenter = loadable(() => import(/* webpackChunkName: "presenter" */ "./Presenter"), {
    fallback: <Loading />
});

const LoadableMeetingRoom = loadable(() => import(/* webpackChunkName: "meetingRoom" */ "./MeetingRoom"), {
    fallback: <Loading />
});

const LoadableCheckout = loadable(() => import(/* webpackChunkName: "checkout" */ "./Checkout"), {
    fallback: <Loading />
});

const LoadableCheckoutEdu = loadable(() => import(/* webpackChunkName: "checkoutEdu" */ "./CheckoutEdu"), {
    fallback: <Loading />
});

const LoadableCopyDeck = loadable(() => import(/* webpackChunkName: "copyDeck" */ "./CopyDeck"), {
    fallback: <Loading />
});

const LoadableInvite = loadable(() => import(/* webpackChunkName: "invite" */ "./Invite"), {
    fallback: <Loading />
});

const LoadableUiHandler = loadable(() => import(/* webpackChunkName: "gdrive" */ "js/react/views/GDrive/UiHandler"), {
    fallback: <Loading />
});

const LoadableSlackRedirectHandler = loadable(() => import(/* webpackChunkName: "slack" */ "js/react/views/Slack/RedirectHandler"), {
    fallback: <Loading />
});

const LoadableProvideAccessPage = loadable(() => import(/* webpackChunkName: "provideAccessPage" */ "./ProvideAccessPage"), {
    fallback: <Loading />
});

function getRedirectSearch(searchParams) {
    const allowedParams = ["roomID"];

    const filteredSearchParams = new URLSearchParams();
    allowedParams.forEach(allowedParamName => {
        if (searchParams.has(allowedParamName)) {
            filteredSearchParams.append(allowedParamName, searchParams.get(allowedParamName));
        }
    });

    if (Array.from(filteredSearchParams.keys()).length === 0) {
        return "";
    }

    return `?${filteredSearchParams.toString()}`;
}

export default function App() {
    // only valid if page was directly navigated to
    const continueUrl = sanitizeRelativeUrl(new URLSearchParams(window.location.search).get("continue") ?? "/");

    return withProviders(
        <Router history={browserHistory}>
            <Switch>
                <Route path={"/signup"} render={({ location }) => {
                    const searchParams = new URLSearchParams(location.search);
                    return (
                        <Authenticate
                            defaultPage="createAccount"
                            affiliate={searchParams.get("affiliate")}
                            popup={searchParams.get("popup")}
                            reason="signup"
                        >
                            <Redirect to={`/${getRedirectSearch(searchParams)}`} />
                        </Authenticate>
                    );
                }} />
                <Route path={"/login"} render={({ location }) => {
                    const searchParams = new URLSearchParams(location.search);
                    return (
                        <Authenticate
                            defaultPage="signIn"
                            popup={searchParams.get("popup")}
                            reason="login"
                        >
                            <Redirect to={`/${getRedirectSearch(searchParams)}`} />
                        </Authenticate>
                    );
                }} />
                <Route path={"/logout"}>
                    <FirebaseAuthInitalizer>
                        <Logout />
                    </FirebaseAuthInitalizer>
                </Route>
                <Route path={"/verify"}>
                    <Authenticate defaultPage="signIn" noVerify>
                        <VerifyEmailReturn nextStep={() => {
                            if (continueUrl.startsWith("/invite/") ||
                                continueUrl.startsWith("/teaminvite/") ||
                                continueUrl.startsWith("/player/")) {
                                // send the request to the server
                                window.location = continueUrl;
                            } else {
                                browserHistory.push(continueUrl);
                            }
                        }} />
                    </Authenticate>
                </Route>
                <Route exact path={["/", "/:id([A-Za-z0-9_-]{20})/:slideNumber?"]}>
                    {/* backbone router handles both the editor and library routes */}
                    <Authenticate defaultPage="signIn">
                        <Onboarding>
                            <LoadableEditorAndLibrary />
                        </Onboarding>
                    </Authenticate>
                </Route>
                <Route path={"/slide-demo/:slideId"}>
                    <Authenticate defaultPage="signIn">
                        <LoadableEditorAndLibrary />
                    </Authenticate>
                </Route>
                <Route path={["/player/:id", "/deck/:id"]}>
                    {/* note: the player relies upon invites to do its auth */}
                    <FirebaseAuthInitalizer>
                        <LoadablePlayer />
                    </FirebaseAuthInitalizer>
                </Route>
                <Route path={"/meetingRoom"}>
                    <FirebaseAuthInitalizer>
                        <LoadableMeetingRoom />
                    </FirebaseAuthInitalizer>
                </Route>
                <Route path={"/presenter"}>
                    <FirebaseAuthInitalizer>
                        <LoadablePresenter />
                    </FirebaseAuthInitalizer>
                </Route>
                <Route path={"/checkout"} render={({ location }) => {
                    const searchParams = new URLSearchParams(location.search);
                    return (
                        <Authenticate defaultPage="createAccount" reason="checkout">
                            <LoadableCheckout
                                plan={searchParams.get("plan")}
                                billingInterval={searchParams.get("billingTerm")}
                                priceId={searchParams.get("price")}
                                displayName={searchParams.get("displayName")}
                                quantity={searchParams.get("quantity")}
                            />
                        </Authenticate>
                    );
                }} />
                <Route path={"/checkout-edu"}>
                    <Authenticate defaultPage="createAccount">
                        <LoadableCheckoutEdu />
                    </Authenticate>
                </Route>
                <Route path={"/copyDeck/:presentationId/:name?"} render={({ location }) => {
                    const searchParams = new URLSearchParams(location.search);
                    return (
                        <Authenticate
                            defaultPage="createAccount"
                            popup={searchParams.get("popup")}
                            reason="copy-deck"
                        >
                            <Onboarding>
                                <LoadableCopyDeck />
                            </Onboarding>
                        </Authenticate>
                    );
                }} />
                <Route path={"/invite/:token"}>
                    <Authenticate
                        defaultPage={serverContext.form == "login" ? "signIn" : "createAccount"}
                        readonlyEmail={serverContext.email}
                        presentationPermission={serverContext.presentationPermission}
                        presentationName={serverContext.presentationName}
                        presentationSharedBy={serverContext.presentationSharedBy}
                        presentationThumbnailUrl={serverContext.presentationThumbnailUrl}
                        reason="presentation-invite"
                    >
                        <LoadableInvite />
                    </Authenticate>
                </Route>
                <Route path={"/teaminvite/:token"}>
                    <Authenticate
                        defaultPage={serverContext.form == "login" ? "signIn" : "createAccount"}
                        readonlyEmail={serverContext.email}
                        teamName={serverContext.teamName}
                        reason="team-invite"
                    >
                        <LoadableInvite />
                    </Authenticate>
                </Route>
                <Route path={["/api/sso/saml/consume", "/testing/fast-login"]}>
                    <FirebaseAuthInitalizer>
                        <SsoConsume />
                    </FirebaseAuthInitalizer>
                </Route>
                <Route path={"/gdrive/uihandler"}>
                    <Authenticate defaultPage="signIn" reason="gdrive" showOnlyGoogle>
                        <LoadableUiHandler />
                    </Authenticate>
                </Route>
                <Route path={"/slack/redirect"}>
                    <Authenticate defaultPage="signIn" reason="slack">
                        <LoadableSlackRedirectHandler />
                    </Authenticate>
                </Route>
                <Route path={"/provideaccess/:notificationId"}>
                    <Authenticate defaultPage="signIn">
                        <LoadableProvideAccessPage />
                    </Authenticate>
                </Route>
                <Route path="*">
                    {({ match }) => (
                        <h1 style={{ color: "red" }}>No &lt;Route&gt; for <code>{match.url}</code></h1>
                    )}
                </Route>
            </Switch>
        </Router>
    );
}

function withProviders(children) {
    return (
        <FirebaseAuthProvider>
            {children}
        </FirebaseAuthProvider>
    );
}
